import React from 'react';
import Grid from '@mui/material/Grid';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Dygraph from 'dygraphs';

type TimeRange = '24h' | '7d' | '30d';

const ExampleDygraph: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;

  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24;
    let stepMs = 60 * 60 * 1000; // 1h
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const base = 10 + Math.sin((points - i) / 3) * 4;
      const noise = (Math.random() - 0.5) * 2;
      out.push([t, Math.max(0, base + noise)]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'Value'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: false,
      legendFormatter: (d: any) => {
        const time = d.x ? d.xHTML : '';
        const parts: string[] = [];
        d.series.forEach((s: any) => {
          if (!s.isVisible) return;
          const val = s.y === null || s.y === undefined ? '—' : (typeof s.y === 'number' ? s.y.toFixed(2) : String(s.y));
          parts.push(`<span style=\"color:${s.color}\">${s.label}: ${val}</span>`);
        });
        return `<div style=\"white-space:nowrap\">${time}${parts.length ? ' | ' + parts.join(' | ') : ''}</div>`;
      },
      strokeWidth: 2,
      colors: [isDark ? '#90CAF9' : '#1976D2'],
      gridLineColor: '#FFFFFF',
      axes: {
        x: { drawAxis: true, drawGrid: true },
        y: { drawAxis: true, drawGrid: true }
      },
      highlightCircleSize: 2,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => {
      g.destroy();
      graphRef.current = null;
    };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={labelsDivRef}
        sx={{
          minHeight: 32,
          mb: 1,
          mr: 2,
          fontSize: 12,
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
          whiteSpace: 'nowrap'
        }}
      />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const ExampleDygraphBar: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const val = Math.max(0, 5 + Math.sin((points - i) / 2) * 3 + (Math.random() - 0.5) * 2);
      out.push([t, parseFloat(val.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  const barPlotter = React.useCallback((e: any) => {
    const ctx = e.drawingContext;
    const points = e.points;
    const yBottom = e.dygraph.toDomYCoord(0);
    ctx.fillStyle = e.color;
    ctx.strokeStyle = e.color;
    let minSep = Infinity;
    for (let i = 1; i < points.length; i++) {
      const sep = points[i].canvasx - points[i - 1].canvasx;
      if (sep < minSep) minSep = sep;
    }
    const barWidth = Math.floor((2 / 3) * minSep);
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const x = p.canvasx - barWidth / 2;
      const y = p.canvasy;
      ctx.fillRect(x, y, barWidth, yBottom - y);
      ctx.strokeRect(x, y, barWidth, yBottom - y);
    }
  }, []);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'Value'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: true,
      strokeWidth: 1,
      colors: [isDark ? '#FFB74D' : '#EF6C00'],
      gridLineColor: '#FFFFFF',
      series: { Value: { plotter: barPlotter as any, fillGraph: true } },
      highlightCircleSize: 0,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, barPlotter, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 60, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const ExampleDygraphArea: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const val = Math.max(0, 8 + Math.cos((points - i) / 2.5) * 2.5 + (Math.random() - 0.5) * 1.5);
      out.push([t, parseFloat(val.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'Area'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: true,
      strokeWidth: 2,
      colors: [isDark ? '#81C784' : '#2E7D32'],
      fillGraph: true,
      gridLineColor: isDark ? '#FFFFFF' : '#CCCCCC',
      axes: { x: { drawAxis: true, drawGrid: true }, y: { drawAxis: true, drawGrid: true } },
      highlightCircleSize: 2,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 60, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220, }} />
    </Box>
  );
};

const ExampleDygraphTwoSeries: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const a = Math.max(0, 7 + Math.sin((points - i) / 2.2) * 2 + (Math.random() - 0.5) * 1.2);
      const b = Math.max(0, 30 + Math.cos((points - i) / 2.8) * 6 + (Math.random() - 0.5) * 3);
      out.push([t, parseFloat(a.toFixed(2)), parseFloat(b.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'A', 'B'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: true,
      strokeWidth: 2,
      colors: [isDark ? '#64B5F6' : '#1976D2', isDark ? '#FF8A65' : '#E64A19'],
      gridLineColor: '#FFFFFF',
      series: {
        A: { axis: 'y1' },
        B: { axis: 'y2' },
      },
      axes: {
        x: { drawAxis: true, drawGrid: true },
        y: { drawAxis: true, drawGrid: true },
        y2: { independentTicks: true },
      },
      highlightCircleSize: 2,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 60, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const ExampleDygraphStep: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const v = Math.max(0, 6 + Math.sin((points - i) / 2.5) * 2 + (Math.random() - 0.5));
      out.push([t, parseFloat(v.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'Step'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: false,
      legendFormatter: (d: any) => {
        const time = d.x ? d.xHTML : '';
        const parts: string[] = [];
        d.series.forEach((s: any) => {
          if (!s.isVisible) return;
          const val = d.x ? (s.y == null ? '—' : (typeof s.y === 'number' ? s.y.toFixed(2) : String(s.y))) : '—';
          parts.push(`<span style=\"color:${s.color}\">${s.label}: ${val}</span>`);
        });
        return `<div style=\"white-space:nowrap\">${time}${parts.length ? ' | ' + parts.join(' | ') : ''}</div>`;
      },
      stepPlot: true,
      strokeWidth: 2,
      colors: [isDark ? '#BA68C8' : '#8E24AA'],
      gridLineColor: '#FFFFFF',
      axes: { x: { drawAxis: true, drawGrid: true }, y: { drawAxis: true, drawGrid: true } },
      highlightCircleSize: 2,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 60, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const ExampleDygraphStackedArea: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const a = Math.max(0, 2 + Math.sin((points - i) / 2) * 1.5 + (Math.random() - 0.5));
      const b = Math.max(0, 3 + Math.cos((points - i) / 3) * 1.2 + (Math.random() - 0.5));
      const c = Math.max(0, 1.5 + Math.sin((points - i) / 1.5) * 0.8 + (Math.random() - 0.5));
      out.push([t, parseFloat(a.toFixed(2)), parseFloat(b.toFixed(2)), parseFloat(c.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'A', 'B', 'C'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: false,
      legendFormatter: (d: any) => {
        const time = d.x ? d.xHTML : '';
        const parts: string[] = [];
        d.series.forEach((s: any) => {
          if (!s.isVisible) return;
          const val = d.x ? (s.y == null ? '—' : (typeof s.y === 'number' ? s.y.toFixed(2) : String(s.y))) : '—';
          parts.push(`<span style=\"color:${s.color}\">${s.label}: ${val}</span>`);
        });
        return `<div style=\"white-space:nowrap\">${time}${parts.length ? ' | ' + parts.join(' | ') : ''}</div>`;
      },
      stackedGraph: true,
      strokeWidth: 2,
      colors: [isDark ? '#64B5F6' : '#1976D2', isDark ? '#81C784' : '#2E7D32', isDark ? '#FFB74D' : '#EF6C00'],
      fillGraph: true,
      gridLineColor: '#FFFFFF',
      axes: { x: { drawAxis: true, drawGrid: true }, y: { drawAxis: true, drawGrid: true } },
      highlightCircleSize: 2,
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 36, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)', whiteSpace: 'nowrap' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const ExampleDygraphScatter: React.FC<{ timeRange: TimeRange }> = ({ timeRange }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const labelsDivRef = React.useRef<HTMLDivElement | null>(null);
  const graphRef = React.useRef<Dygraph | null>(null);
  const theme = useTheme() as any;
  const isDark = theme?.name === 'dark' || theme?.palette?.mode === 'dark';

  const buildData = React.useCallback(() => {
    const now = new Date();
    let points = 24; let stepMs = 60 * 60 * 1000;
    if (timeRange === '7d') { points = 7; stepMs = 24 * 60 * 60 * 1000; }
    if (timeRange === '30d') { points = 30; stepMs = 24 * 60 * 60 * 1000; }
    const out: (Date | number)[][] = [];
    for (let i = points - 1; i >= 0; i--) {
      const t = new Date(now.getTime() - i * stepMs);
      const v = Math.max(0, 10 + (Math.random() - 0.5) * 8);
      out.push([t, parseFloat(v.toFixed(2))]);
    }
    return out as any;
  }, [timeRange]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const data = buildData();
    const g = new Dygraph(containerRef.current, data as any, {
      labels: ['Time', 'Points'],
      height: 220,
      legend: 'always',
      labelsDiv: labelsDivRef.current || undefined,
      labelsSeparateLines: true,
      drawPoints: true,
      strokeWidth: 0,
      pointSize: 3,
      colors: [isDark ? '#F06292' : '#C2185B'],
      gridLineColor: isDark ? 'rgba(214, 203, 203, 0.92)' : 'rgba(224, 217, 217, 0.35)',
      axes: { x: { drawAxis: true, drawGrid: true }, y: { drawAxis: true, drawGrid: true } },
      hideOverlayOnMouseOut: true,
    });
    graphRef.current = g;
    return () => { g.destroy(); graphRef.current = null; };
  }, [buildData, isDark]);

  return (
    <Box sx={{ width: '100%', height: 260, display: 'flex', flexDirection: 'column' }}>
      <Box ref={labelsDivRef} sx={{ minHeight: 48, mb: 1, fontSize: 12, color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }} />
      <Box ref={containerRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

const mockKpis = [
  { label: 'Urządzenia online', value: 128, color: 'success' as const },
  { label: 'Urządzenia offline', value: 7, color: 'error' as const },
  { label: 'W serwisie', value: 5, color: 'warning' as const },
  { label: 'Alerty 24h', value: 19, color: 'info' as const }
];

const Dashboard: React.FC = () => {
  const [timeRange, setTimeRange] = React.useState<TimeRange>('7d');
  const timeRanges: TimeRange[] = ['24h', '7d', '30d'];
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {mockKpis.map((kpi) => (
          <Grid key={kpi.label} item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      {kpi.label}
                    </Typography>
                    <Typography variant="h4">{kpi.value}</Typography>
                  </Box>
                  <Chip color={kpi.color} label={kpi.color.toUpperCase()} size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>Zakres czasu:</Typography>
                <Stack direction="row" spacing={1}>
                  {timeRanges.map(r => (
                    <Chip
                      key={r}
                      label={r}
                      color={timeRange === r ? 'primary' : 'default'}
                      size="small"
                      onClick={() => setTimeRange(r)}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Wykres (Dygraphs)</Typography>
              <ExampleDygraph timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Słupkowy</Typography>
              <ExampleDygraphBar timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Obszarowy</Typography>
              <ExampleDygraphArea timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Dwie serie (Y2)</Typography>
              <ExampleDygraphTwoSeries timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Krokowy</Typography>
              <ExampleDygraphStep timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Stacked area</Typography>
              <ExampleDygraphStackedArea timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Scatter</Typography>
              <ExampleDygraphScatter timeRange={timeRange} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;


